import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types';
import { ArrowRight, Loader2, CheckCircle } from 'lucide-react';
export function SignupForm() {
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'tenant' as UserRole,
    phone: ''
  });
  const {
    login,
    isLoading
  } = useAuth();
  const navigate = useNavigate();
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (step < 2) {
      setStep(step + 1);
    } else {
      await login(formData.email, formData.role);
      // Redirect to onboarding for new users
      if (formData.role === 'tenant') {
        navigate('/tenant/onboarding');
      } else if (formData.role === 'landlord') {
        navigate('/landlord/onboarding');
      } else {
        navigate('/admin/dashboard');
      }
    }
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8 relative overflow-hidden" data-id="element-562">
      {/* Background Decoration */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-10" data-id="element-563">
        <div className="absolute top-[-10%] right-[-10%] w-96 h-96 bg-green-200/30 rounded-full blur-3xl" data-id="element-564"></div>
        <div className="absolute bottom-[-10%] left-[-10%] w-96 h-96 bg-blue-200/30 rounded-full blur-3xl" data-id="element-565"></div>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md" data-id="element-566">
        <div className="flex justify-center mb-8" data-id="element-567">
          <Link to="/" data-id="element-568">
            <img src="https://cdn.magicpatterns.com/uploads/7EjErFNEa8ZC1es35aEHqt/RENTA_logo.png" alt="Renta" className="h-14 w-auto drop-shadow-sm hover:scale-105 transition-transform" data-id="element-569" />
          </Link>
        </div>
        <h2 className="text-center text-3xl font-extrabold text-gray-900 tracking-tight" data-id="element-570">
          Create your account
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600" data-id="element-571">
          Join the AI-powered rent ecosystem
        </p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md" data-id="element-572">
        <div className="bg-white py-8 px-4 shadow-xl shadow-gray-200/50 sm:rounded-2xl sm:px-10 border border-gray-100" data-id="element-573">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8" data-id="element-574">
            <div className={`h-2 w-1/3 rounded-full ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200'} mr-2 transition-colors duration-300`} data-id="element-575"></div>
            <div className={`h-2 w-1/3 rounded-full ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'} transition-colors duration-300`} data-id="element-576"></div>
          </div>

          <form className="space-y-6" onSubmit={handleSubmit} data-id="element-577">
            {step === 1 ? <>
                <div data-id="element-578">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-579">
                    I want to
                  </label>
                  <div className="grid grid-cols-2 gap-3" data-id="element-580">
                    <button type="button" onClick={() => setFormData({
                  ...formData,
                  role: 'tenant'
                })} className={`p-4 border rounded-xl flex flex-col items-center text-center transition-all ${formData.role === 'tenant' ? 'border-blue-600 bg-blue-50 ring-1 ring-blue-600' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-581">
                      <span className="text-2xl mb-2" data-id="element-582">🏠</span>
                      <span className={`font-bold text-xs ${formData.role === 'tenant' ? 'text-blue-900' : 'text-gray-900'}`} data-id="element-583">
                        Pay Rent
                      </span>
                      <span className="text-[10px] text-gray-500 mt-1" data-id="element-584">
                        Monthly
                      </span>
                    </button>

                    <button type="button" onClick={() => setFormData({
                  ...formData,
                  role: 'landlord'
                })} className={`p-4 border rounded-xl flex flex-col items-center text-center transition-all ${formData.role === 'landlord' ? 'border-green-600 bg-green-50 ring-1 ring-green-600' : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-585">
                      <span className="text-2xl mb-2" data-id="element-586">🔑</span>
                      <span className={`font-bold text-xs ${formData.role === 'landlord' ? 'text-green-900' : 'text-gray-900'}`} data-id="element-587">
                        Collect Rent
                      </span>
                      <span className="text-[10px] text-gray-500 mt-1" data-id="element-588">
                        Upfront
                      </span>
                    </button>
                  </div>
                </div>

                <div data-id="element-589">
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700" data-id="element-590">
                    Full Name
                  </label>
                  <div className="mt-1" data-id="element-591">
                    <input id="name" name="name" type="text" required value={formData.name} onChange={e => setFormData({
                  ...formData,
                  name: e.target.value
                })} className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm" placeholder="John Doe" data-id="element-592" />
                  </div>
                </div>
              </> : <>
                <div data-id="element-593">
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700" data-id="element-594">
                    Email address
                  </label>
                  <div className="mt-1" data-id="element-595">
                    <input id="email" name="email" type="email" required value={formData.email} onChange={e => setFormData({
                  ...formData,
                  email: e.target.value
                })} className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm" placeholder="you@example.com" data-id="element-596" />
                  </div>
                </div>

                <div data-id="element-597">
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700" data-id="element-598">
                    Phone Number
                  </label>
                  <div className="mt-1" data-id="element-599">
                    <input id="phone" name="phone" type="tel" required value={formData.phone} onChange={e => setFormData({
                  ...formData,
                  phone: e.target.value
                })} className="appearance-none block w-full px-4 py-3 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent sm:text-sm" placeholder="+234..." data-id="element-600" />
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100" data-id="element-601">
                  <div className="flex items-start" data-id="element-602">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 mr-2 flex-shrink-0" data-id="element-603" />
                    <p className="text-xs text-gray-600" data-id="element-604">
                      By clicking "Create Account", you agree to our Terms of
                      Service and Privacy Policy. Your data is secure and
                      encrypted.
                    </p>
                  </div>
                </div>
              </>}

            <div data-id="element-605">
              <button type="submit" disabled={isLoading} className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 transition-all hover:shadow-lg hover:-translate-y-0.5" data-id="element-606">
                {isLoading ? <Loader2 className="h-5 w-5 animate-spin" data-id="element-607" /> : step === 1 ? 'Continue' : 'Create Account'}
                {!isLoading && <ArrowRight className="ml-2 h-4 w-4" data-id="element-608" />}
              </button>
            </div>

            {step === 2 && <div className="text-center" data-id="element-609">
                <button type="button" onClick={() => setStep(1)} className="text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors" data-id="element-610">
                  ← Back
                </button>
              </div>}
          </form>
        </div>

        <p className="mt-8 text-center text-sm text-gray-500" data-id="element-611">
          Already have an account?{' '}
          <Link to="/login" className="font-bold text-blue-600 hover:text-blue-500" data-id="element-612">
            Sign in
          </Link>
        </p>
      </div>
    </div>;
}